import { EventMapCore, useNavigation } from '@react-navigation/native'
import { createEmptyPost, createEmptyStep } from 'components/pages/WorkoutEditor/creationHelpers'
import { UpscoreBadge } from 'components/widgets/UpscoreBadge'
import { MessageTexts } from 'constants/messageTexts'
import { IS_IOS, IS_WEB } from 'constants/platform'
import { PostTypes } from 'constants/postTypes'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'

import { useCurrentUser } from 'hooks/useCurrentUser'
import { useNavigateBackOrHome } from 'hooks/useNavigateBackOrHome'
import { usePost } from 'hooks/usePost'

import { externalLinks, links } from 'utils/constants'
import { PostPageError } from 'utils/error/PostPageError'
import { getFileUploadSignature } from 'utils/firebase'
import { getFileTypeFromMimeType, getMimeTypeAndFileFromFileOrMedia } from 'utils/media'
import { Toast } from 'utils/toast'
import { computeNewUpscore } from 'utils/upscore'

import { useCreatePostModal } from 'contexts/CreatePostModalContext'
import { useFocusMode } from 'contexts/FocusModeContext'

import { InProgressPost, InProgressPostStep } from 'types/post'
import { Data } from 'types/query'

import { useQueryClient } from './useQueryClient'

export const useWorkoutEditor = ({
  postType,
  isEditing,
  postId,
  programId,
  activityGroup,
  activityType,
  drawerNavigationRef,
  onPublish,
}: {
  postType: PostTypes
  isEditing: boolean
  postId?: string
  programId?: string
  activityGroup?: string
  activityType?: string
  drawerNavigationRef?: any
  onPublish?: (id?: string) => void
}) => {
  const queryClient = useQueryClient()
  const createPostModalRef = useCreatePostModal()
  const navigation = useNavigation<any>()
  const navigateBackOrHome = useNavigateBackOrHome()
  const { data: currentUser } = useCurrentUser()
  const { createPost, data: postData, editPost } = usePost(postId)
  const hasUnsavedChanges = useRef(false)

  const navigateToUpscorePage = () => {
    drawerNavigationRef.current.navigate(links.UPSCORE_PAGE, { userId: currentUser?.id })
  }

  const navigateAfterDiscardChanges = useRef(() => null)

  const focusMode = useFocusMode()
  const isFocusMode = useMemo(() => focusMode.group !== undefined, [focusMode.group])
  const selectedProgram = useMemo(
    () =>
      focusMode.group?.trainer?.id === currentUser?.id ? 'upspace' : focusMode.group?.id ?? '',
    [currentUser?.id, focusMode.group?.id, focusMode.group?.trainer?.id],
  )

  const [openModalName, setOpenModalName] = useState<'delete' | 'discardChanges' | undefined>(
    undefined,
  )

  const closeDeleteModal = useCallback(
    (wasPostDeleted?: boolean) => {
      setOpenModalName(undefined)

      if (wasPostDeleted) {
        hasUnsavedChanges.current = false
        navigateBackOrHome()
      }
    },
    [navigateBackOrHome],
  )

  const closeDiscardModal = useCallback(() => {
    setOpenModalName(undefined)
  }, [])

  const existingPostData = postData || null
  let existingViewerCategories: string[] = []
  if (existingPostData) {
    if (existingPostData.viewerCategories) {
      existingViewerCategories = [...existingPostData.viewerCategories]
    }
    if (existingPostData.isDraft) {
      existingViewerCategories.push('draft')
    }
  }

  const existingInProgressPost: InProgressPost | undefined = existingPostData
    ? {
        type: existingPostData.type,
        isDraft: existingPostData.isDraft,
        file: existingPostData.thumbnail,
        steps: existingPostData.steps,
        title: existingPostData.title,
        description: existingPostData.description,
        visibility: existingPostData.isPremium ? 'premium' : 'public',
        hasSteps: existingPostData.steps?.length > 0,
        isPending: existingPostData.isPending,
        tagGroups: existingPostData.tagGroups,
        programIds: existingPostData.programIds,
        activityGroup: existingPostData.activityGroup,
        activityType: existingPostData.activityType,
        combinedCategoriesAndProgramIds: existingPostData.combinedCategoriesAndProgramIds,
        publishedToProgramIds: existingPostData.publishedToProgramIds,
        viewerCategories: existingViewerCategories,
      }
    : undefined

  const [postFormData, setPostFormData] = useState<InProgressPost>(
    existingInProgressPost ?? {
      ...createEmptyPost(postType),
      publishedToProgramIds: programId ? [programId] : [],
      combinedCategoriesAndProgramIds:
        // eslint-disable-next-line no-nested-ternary
        postType === PostTypes.WORKOUT
          ? ['upspace']
          : isFocusMode
          ? [selectedProgram]
          : ['followers'],
    },
  )

  const [processingPublish, setProcessingPublish] = useState<boolean>(false)

  const handlePublish = async () => {
    if (processingPublish || !currentUser?.id || !postFormData || !validateInput()) return

    const publishHandler = isEditing ? editPost : createPost

    const hasDraftCategory = postFormData.combinedCategoriesAndProgramIds?.includes('draft')
    if (hasDraftCategory && postFormData.combinedCategoriesAndProgramIds?.length === 1) {
      postFormData.combinedCategoriesAndProgramIds = []
    }

    setProcessingPublish(true)

    publishHandler.mutate(
      {
        isDraft: hasDraftCategory ?? false,
        postData: postFormData,
        userId: currentUser.id,
      },
      {
        onSuccess: (data: any) => {
          setProcessingPublish(false)
          hasUnsavedChanges.current = false
          if (postType === PostTypes.ACTIVITY) {
            createPostModalRef?.current?.dismiss()

            if (!currentUser.dailyCompletions || currentUser.dailyCompletions < 4) {
              queryClient.setQueryData(['user', currentUser.id], (data: Data) => {
                const newDailyCompletions = data.dailyCompletions ? data.dailyCompletions + 1 : 1
                if (data) {
                  // eslint-disable-next-line no-param-reassign
                  data.upscore = computeNewUpscore(currentUser.upscore, postFormData)
                  // eslint-disable-next-line no-param-reassign
                  data.dailyCompletions = newDailyCompletions
                }
                return data
              })

              Toast.success(
                '',
                'You just completed an activity! Way to Go!\nPoints have been added to your Upscore.',
                undefined,
                {},
                true,
                'bottom',
                navigateToUpscorePage,
                'View',
                true,
                '',
                UpscoreBadge({ showOutline: false, text: '+1' }),
              )
            }
          } else {
            onPublish && onPublish(data)
            navigateBackOrHome()
          }
        },
        onError: (error: unknown) => {
          setProcessingPublish(false)
          const { message, pageIndex, isMediaMissing } = error as PostPageError
          if (isMediaMissing) {
            Toast.error('', `Media missing for step ${pageIndex}`)
          } else {
            // eslint-disable-next-line no-nested-ternary
            const title = pageIndex
              ? postType === PostTypes.WORKOUT
                ? `Failed to process step ${pageIndex}`
                : `Failed to process image at step ${pageIndex}`
              : `Failed to process post`
            Toast.error(title, message)
          }
        },
      },
    )
  }

  const validateInput = (): boolean => {
    if (postType === PostTypes.WORKOUT) {
      const hasTitle = !!postFormData?.title?.length
      const hasThumbnail = !!postFormData?.file
      const hasSteps = !!postFormData?.steps?.length

      if (!hasTitle || !hasThumbnail || !hasSteps) {
        Toast.error(
          '',
          'You must add Title, Thumbnail, and at least one step before sharing.',
          false,
          { autoHide: true },
          true,
          'bottom',
          undefined,
          undefined,
          true,
          '‼️',
        )
        return false
      }
    }

    if (postType === PostTypes.ACTIVITY && !activityGroup?.length && !activityType?.length) {
      console.log('Post of type "Activity" must have "activityGroup" and "activityType" fields')
      return false
    }

    if (postType === PostTypes.POST || postType === PostTypes.ACTIVITY) {
      const inputText = postFormData.description
      const hasDescription = inputText ? inputText.trim().length : false

      if (!hasDescription && postFormData.steps[0].file === undefined) {
        Toast.error(
          'Invalid input',
          'Must contain description or images',
          undefined,
          undefined,
          true,
          'bottom',
        )
        return false
      }
    }

    return true
  }

  const saveDetails = useCallback(
    async (values: Partial<InProgressPost>) => {
      setPostFormData((currentPostFormData) => {
        const formData = {
          ...currentPostFormData,
          ...values,
        }

        if (postType !== PostTypes.WORKOUT) {
          const hasFollowersCategory = values.combinedCategoriesAndProgramIds?.includes('followers')
          const hasFriendsCategory = values.combinedCategoriesAndProgramIds?.includes('friends')
          formData.visibility = hasFollowersCategory || hasFriendsCategory ? 'public' : 'premium'
        }

        return formData
      })
      hasUnsavedChanges.current = true
    },
    [postType],
  )

  const saveStep = useCallback(
    (stepIndex: number) => async (values: Partial<InProgressPostStep>) => {
      if (values.file && values.isAudioEnabled !== undefined) {
        const { mediaMimeType } = getMimeTypeAndFileFromFileOrMedia(values.file)
        const mediaType = getFileTypeFromMimeType(mediaMimeType)

        if (mediaType !== 'unknown') {
          const hashTimestamp = Date.now().toString()
          const hashableBody = {
            upload_preset: '',
            timestamp: hashTimestamp,
            async: false.toString(),
          }

          if (mediaType === 'image') {
            hashableBody.upload_preset = 'postImage'
          } else if (mediaType === 'video') {
            const guid = uuidv4()
            const publicId = `${currentUser?.id}/${guid}`
            hashableBody.public_id = publicId
            // eslint-disable-next-line no-param-reassign
            values.publicId = publicId

            hashableBody.async = true.toString()
            hashableBody.notification_url = `${externalLinks.UPSPACE_FUNCTIONS_FULLPATH}/videoUploadComplete`
            if (values.isAudioEnabled) {
              hashableBody.upload_preset = 'postVideo'
            } else {
              hashableBody.upload_preset = 'silentPostVideo'
            }
          }

          const { data: signature } = await getFileUploadSignature(hashableBody)
          // eslint-disable-next-line no-param-reassign
          values.signature = signature
          // eslint-disable-next-line no-param-reassign
          values.timeStamp = hashTimestamp
        }
      }

      setPostFormData((currentPostFormData) => {
        const currentSteps = [...currentPostFormData.steps]
        return {
          ...currentPostFormData,
          steps: [
            ...currentSteps.slice(0, stepIndex),
            { ...currentSteps[stepIndex], ...values },
            ...currentSteps.slice(stepIndex + 1),
          ],
        }
      })
      hasUnsavedChanges.current = true
    },
    [currentUser?.id],
  )

  const addStep = useCallback((insertAtIndex: number) => {
    setPostFormData((currentPostFormData) => {
      const newSteps = [
        ...currentPostFormData.steps.slice(0, insertAtIndex),
        createEmptyStep(),
        ...currentPostFormData.steps.slice(insertAtIndex),
      ]
      return { ...currentPostFormData, steps: newSteps }
    })
  }, [])

  const deleteStep = useCallback((stepIndex: number) => {
    setPostFormData((currentPostFormData) => {
      const newSteps = [
        ...currentPostFormData.steps.slice(0, stepIndex),
        ...currentPostFormData.steps.slice(stepIndex + 1),
      ]
      return { ...currentPostFormData, steps: newSteps }
    })

    hasUnsavedChanges.current = true
  }, [])

  const deleteStepAtIndex = useCallback((stepIndex: number) => {
    setPostFormData((currentPostFormData) => {
      const newSteps = [
        ...currentPostFormData.steps.slice(0, stepIndex),
        ...currentPostFormData.steps.slice(stepIndex + 1),
      ]
      return { ...currentPostFormData, steps: newSteps }
    })
    hasUnsavedChanges.current = true
  }, [])

  const discardChanges = useCallback(() => {
    closeDiscardModal()
    hasUnsavedChanges.current = false
    navigateAfterDiscardChanges.current()
  }, [closeDiscardModal, navigateAfterDiscardChanges])

  const saveMultipleSteps = async (values: Partial<InProgressPostStep>[]) => {
    values.map(async (value) => {
      if (value.file) {
        const { mediaMimeType } = getMimeTypeAndFileFromFileOrMedia(value?.file)
        const mediaType = getFileTypeFromMimeType(mediaMimeType)

        if (mediaType !== 'unknown') {
          const hashTimestamp = Date.now().toString()
          const hashableBody = {
            upload_preset: '',
            timestamp: hashTimestamp,
            async: false.toString(),
          }

          if (mediaType === 'image') {
            hashableBody.upload_preset = 'postImage'
          } else if (mediaType === 'video') {
            const guid = uuidv4()
            const publicId = `${currentUser?.id}/${guid}`
            hashableBody.public_id = publicId
            // eslint-disable-next-line no-param-reassign
            value.publicId = publicId

            hashableBody.async = true.toString()
            hashableBody.notification_url = `${externalLinks.UPSPACE_FUNCTIONS_FULLPATH}/videoUploadComplete`
            if (value.isAudioEnabled) {
              hashableBody.upload_preset = 'postVideo'
            } else {
              hashableBody.upload_preset = 'silentPostVideo'
            }
          }

          const { data: signature } = await getFileUploadSignature(hashableBody)
          // eslint-disable-next-line no-param-reassign
          value.signature = signature
          // eslint-disable-next-line no-param-reassign
          value.timeStamp = hashTimestamp
        }
      }
    })

    setPostFormData((currentPostFormData) => {
      return {
        ...currentPostFormData,
        steps: values,
      }
    })
    hasUnsavedChanges.current = true
  }

  useEffect(() => {
    if (isEditing || existingInProgressPost) return

    switch (postType) {
      case PostTypes.POST: {
        setPostFormData((currentPostFormData) => ({
          ...currentPostFormData,
          title: '',
          description: '',
          visibility: isFocusMode ? 'premium' : 'public',
        }))
        break
      }
      case PostTypes.ACTIVITY: {
        setPostFormData((currentPostFormData) => ({
          ...currentPostFormData,
          title: '',
          description: '',
          visibility: isFocusMode ? 'premium' : 'public',
          activityGroup,
          activityType,
        }))
        break
      }
      default: {
        break
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityGroup, activityType, isEditing, isFocusMode, postType])

  useEffect(() => {
    navigation.addListener('beforeRemove', (event: Event & EventMapCore<any>['beforeRemove']) => {
      if (!hasUnsavedChanges.current) {
        return
      }

      IS_WEB && postType !== PostTypes.ACTIVITY && event.preventDefault()
      navigateAfterDiscardChanges.current = () => navigation.dispatch(event.data.action)

      setOpenModalName('discardChanges')
    })
  }, [navigation, postType])

  useEffect(() => {
    const isOwner = currentUser?.id === postData?.creator.id
    if (isEditing && !isOwner) {
      Toast.error('No access', 'You are not the owner of this post')
      navigateBackOrHome()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, postData, isEditing])

  useEffect(() => {
    if (!IS_IOS) return

    const files = postFormData.steps.map((step) => step.file)

    if (!files || !files?.length) return

    const paths = files.map((f) => f?.path).filter((p) => p !== undefined)

    const hasDuplicates = paths.length !== new Set(paths).size
    hasDuplicates &&
      Toast.error(
        'Duplicated Video',
        MessageTexts.DUPLICATED_VIDEOS,
        false,
        { autoHide: false },
        true,
        'bottom',
        undefined,
        undefined,
        true,
      )
  }, [postFormData.steps])

  return {
    postFormData,
    saveMultipleSteps,
    createPostLoading: createPost.isLoading,
    editPostLoading: editPost.isLoading,
    addStep,
    handlePublish,
    deleteStep,
    saveDetails,
    saveStep,
    deleteStepAtIndex,
    openModalName,
    closeDeleteModal,
    closeDiscardModal,
    discardChanges,
  }
}
